import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Stethoscope } from "lucide-react";
import { POLICY_SECTIONS } from "@/lib/privacyPolicy";

const CONTACT = "dr.sangramj@gmail.com";

function PolicyTable({ table, id }) {
  return (
    <>
      <div className="mt-4 space-y-3 sm:hidden" data-testid={`privacy-cards-${id}`}>
        {table.rows.map((r, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="font-semibold text-slate-900 text-sm">{r[0]}</div>
            {r.slice(1).map((c, j) => (
              <div key={j} className="mt-2 text-sm">
                <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500">{table.head[j + 1]}</div>
                <div className="text-slate-700 leading-relaxed">{c}</div>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="mt-4 hidden sm:block" data-testid={`privacy-table-${id}`}>
      <table className="w-full text-sm border-collapse bg-white rounded-xl overflow-hidden border border-slate-200">
        <thead className="bg-slate-50 text-left">
          <tr>{table.head.map(h => <th key={h} className="px-4 py-3 font-semibold text-slate-700 border-b border-slate-200">{h}</th>)}</tr>
        </thead>
        <tbody>
          {table.rows.map((r, i) => (
            <tr key={i} className="align-top border-b border-slate-100 last:border-0">
              {r.map((c, j) => <td key={j} className={`px-4 py-3 ${j === 0 ? "font-medium text-slate-800" : "text-slate-600"}`}>{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </>
  );
}

function Section({ s }) {
  return (
    <section id={s.id} className="scroll-mt-24 pt-10" data-testid={`privacy-section-${s.id}`}>
      <h2 className="font-display text-base md:text-lg font-bold tracking-tight text-slate-900 uppercase">{s.title}</h2>
      {(s.paragraphs || []).map((p, i) => <p key={i} className="mt-3 text-sm sm:text-base leading-relaxed text-slate-700">{p}</p>)}
      {s.table && <PolicyTable table={s.table} id={s.id} />}
      {s.list && (
        <ul className="mt-3 space-y-2 list-disc pl-5 text-sm sm:text-base text-slate-700 leading-relaxed">
          {s.list.map(li => <li key={li}>{li}</li>)}
        </ul>
      )}
      {(s.after || []).map((p, i) => <p key={i} className="mt-3 text-sm sm:text-base leading-relaxed text-slate-700">{p}</p>)}
    </section>
  );
}

export default function PrivacyPage() {
  useEffect(() => { document.title = "Privacy Policy · LocumConnect"; window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-screen bg-[#FAFAFA] text-slate-900" data-testid="privacy-page">
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-5 py-4">
          <Link to="/" className="flex items-center gap-2" data-testid="privacy-home-link">
            <div className="w-8 h-8 rounded-lg bg-emerald-700 flex items-center justify-center">
              <Stethoscope className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display text-lg font-bold tracking-tight">LocumConnect</span>
          </Link>
          <a href={`mailto:${CONTACT}`} className="text-sm text-emerald-800 hover:underline" data-testid="privacy-contact-link">Contact</a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-10 sm:py-14">
        <div className="text-xs uppercase tracking-[0.2em] text-emerald-800">Legal</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-2" data-testid="privacy-title">LocumConnect Privacy Policy</h1>
        <p className="mt-3 text-sm text-slate-500" data-testid="privacy-updated">Last updated: 2 November 2026</p>

        <nav className="mt-8 flex flex-wrap gap-2" aria-label="Sections">
          {POLICY_SECTIONS.map(s => (
            <a key={s.id} href={`#${s.id}`} className="text-xs px-3 py-1.5 rounded-full border border-slate-200 bg-white text-slate-600 hover:border-emerald-700 hover:text-emerald-800 transition-colors" data-testid={`privacy-nav-${s.id}`}>
              {s.short}
            </a>
          ))}
        </nav>

        {POLICY_SECTIONS.map(s => <Section key={s.id} s={s} />)}
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-5 py-8 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500">
          <span>© {new Date().getFullYear()} LocumConnect</span>
          <Link to="/" className="hover:text-emerald-800" data-testid="privacy-footer-home">Back to home</Link>
        </div>
      </footer>
    </div>
  );
}
